import React from 'react'
import { QuestionProps } from './types'
import { useController, Formalities } from 'formalities'
import QuestionScreen from './QuestionScreen'
import QuestionHeader from './QuestionHeader'
import { AnswerRequest, MultichoiceQuestion } from 'typescript-fetch-api'
import { useKeyboardNavigation } from '../hooks'
import { uniqueToastError } from 'modules/common/functions'

interface FormState {
	answer: number
}

export default function QuestionSelect(props: QuestionProps<MultichoiceQuestion>) {
	const { section, question, questionIndex } = props

	useKeyboardNavigation(onPrev, onNext)
	
	const options = question.options ? question.options.map((option, optionIndex) => {
		return ({
			value: optionIndex,
			text: option,
		})
	}) : []

	options.unshift({
		value: -1,
		text: 'Choose one…',
	})

	const controller = useController<FormState>({
		answer: question.answerIndex === undefined ? -1 : question.answerIndex,
	})

	controller.addChangeListener(form => save(form))

	async function save(form: FormState) {
		if (form.answer === -1 && !question.optional) {
			return false
		}

		const answerReq: AnswerRequest = {
			questionId: question.id,
			intValue: form.answer !== -1 ? form.answer : undefined,
		}

		if (answerReq.intValue === question.answerIndex) {
			return
		}
		
		const saved = await props.onAnswerQuestion(answerReq)
		if (!saved) {
			controller.snapshot('answer').setValue(question.answerIndex === undefined ? -1 : question.answerIndex)
		}
	}

	function onNext(evt?: React.FormEvent) {
		evt && evt.preventDefault()
		
		const form = controller.snapshot().value
		if (form.answer === -1 && !question.optional) {
			uniqueToastError('Please choose an answer before continuing.')
			return false
		}
		props.onNext()
	}

	function canProgress() {
		const form = controller.snapshot().value
		if (form.answer === -1 && !question.optional) {
			return false
		} else {
			return true
		}
	}

	function onPrev(evt?: React.MouseEvent) {
		evt && evt.preventDefault()
		props.onPrev()
	}

	function onExit(evt: React.MouseEvent) {
		evt.preventDefault()
		props.onExit()
	}

	return (
		<QuestionScreen section={section} question={question}>
			<div className="survey-question">
				<QuestionHeader question={question} questionIndex={questionIndex} />
				{
					question.options &&
					<div className="form-row">
						<div className="form-input -select">
							<Formalities.Select className="select" controller={controller} prop="answer" options={options}/>
						</div>
					</div>
				}
					
				<div className="footer-actions">
					<div className="button-group">
						<a href="/" className="button-link -secondary -back" onClick={onPrev}>Back</a>
						<a href="/" className="button-link -text" onClick={onExit}>Exit survey</a>
					</div>
					<div className="button-group -right">
						<input type="submit" className={'button-link -action -next ' + (canProgress() ? '' : '-disabled')} value="Next" onClick={onNext} />
					</div>
				</div>
				<div className="keyboard-nav">
					<p className="option">You can also use your <span className="key">&larr;</span> <span className="key">&rarr;</span> arrow keys to navigate forward &amp; back</p>
				</div>
			</div>
		</QuestionScreen>
	)
}
